import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import Wire from './featured-wires-components/wire';
import {
  DesktopSeparator,
  SectionContainer,
  SectionTitle,
  SectionDetails,
} from './styles';
import useGetWiresFeaturedByContributor from '../../../services/wires/featured-by-contributor-service';
import useGetWiresFeaturedByFund from '../../../services/wires/featured-by-fund-service';
import { scrollIfHashMatch } from '../navigation/utils';
import useNavigationStateStore from '../navigation/navigation-state-hook';

// isSectionCollapsed
const isSC = d => (
  (d.theme.isLarge && d.items === 3)
  || (!d.theme.isLarge && !d.isBelow480 && d.items === 1)
  || d.isBelow480
);

const Container = styled(SectionContainer)`
  flex-direction: ${props => (isSC(props) ? 'column' : 'row')} !important;
`;

const Title = styled(SectionTitle)`
  margin-bottom: 32px !important;

  border-bottom: ${props => (props.isBelow480 ? '1px solid #dbe1e8' : 'none')} !important;
  width: ${props => (isSC(props) ? 100 : 33)}% !important;
`;

const Details = styled(SectionDetails)`
  width: ${props => (isSC(props) ? 100 : 67)}% !important;

  ${props => (props.theme.isLarge ? `
    flex-direction: row;
    justify-content: space-between;

    & > section {
      width: ${100 / props.items}%;
    }

    & > section:not(:last-child) {
      margin-right: 26px;
    }
  ` : `
    flex-direction: column;
    align-items: center;
    & > section {
      width: 100%;
    }
    & > section:not(:last-child) {
      margin-bottom: 40px;
    }
  `)};
`;

const SSeparator = styled(DesktopSeparator)`
  margin-bottom: 56px;
`;

const sectionName = 'featured-wires';

export default function FeaturedWires({ objectId, type, separator }) {
  const isBelow480 = useMediaQuery({ query: '(max-width: 479px)' });
  const useGetWires = type === 'profile' ? useGetWiresFeaturedByContributor : useGetWiresFeaturedByFund;
  const [{ data }, getFeaturedWires] = useGetWires();
  const [wires, setWires] = React.useState([]);
  const clickEventProps = {
    id: objectId,
    type,
    name: type === 'profile' ? 'contributor_profile_featured_wire_view' : 'fund_profile_featured_wire_view',
  };
  const set = useNavigationStateStore(state => state.set);

  useChangeAwareEffect(({ isMount }) => {
    isMount && scrollIfHashMatch(sectionName);
  }, {});

  React.useEffect(() => {
    objectId && getFeaturedWires(objectId);
  }, [objectId, getFeaturedWires]);

  React.useEffect(() => {
    const featuredWires = _.get(data, 'wires', []);
    const featuredWiresToShow = _.take(_.filter(featuredWires, w => !!w.primaryImageUrl), 3);

    setWires(featuredWiresToShow);
    set(state => {
      state.hasFeaturedWires = featuredWiresToShow.length > 0;
    });
  }, [data, set]);

  if (wires.length === 0) {
    return null;
  }

  const title = `featured wire${wires.length > 1 ? 's' : ''}`;

  const baseProps = { items: wires.length, isBelow480 };

  return (
    <>
      <Container {...baseProps} name={sectionName}>
        <Title {...baseProps}>{title}</Title>
        <Details {...baseProps}>
          {wires.map(wire => (
            <Wire
              {...baseProps}
              key={`featured-wire-${wire.id}`}
              wire={wire}
              mention={type === 'profile' && objectId !== wire.profileId}
              clickEventProps={clickEventProps}
            />
          ))}
        </Details>
      </Container>
      {separator && <SSeparator />}
    </>
  );
}

FeaturedWires.propTypes = {
  objectId: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  separator: PropTypes.bool,
};

FeaturedWires.defaultProps = {
  separator: false,
};
